import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Edit3, Search } from "lucide-react";
import { toast } from "sonner";
import Pagination, { usePagination } from "@/components/Pagination";

const EMPTY = { email: "", name: "", password: "", role_id: "", active: true, allowed_projects: [] };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [filterRole, setFilterRole] = useState("__all__");
  const [filterActive, setFilterActive] = useState("__all__"); // __all__, active, inactive
  const [filterProject, setFilterProject] = useState("__all__"); // __all__, __none__ (all-access), or a project name

  const load = async () => {
    const [u, r, p] = await Promise.all([
      api.get("/users"),
      api.get("/roles"),
      api.get("/projects").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data);
    setRoles(r.data);
    setProjects(p.data || []);
  };

  useEffect(() => { load(); }, []);

  const filteredUsers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      // Search filter
      if (needle) {
        const ap = Array.isArray(u.allowed_projects) ? u.allowed_projects.join(" ") : "";
        const hay = `${u.name || ""} ${u.email || ""} ${u.role_name || ""} ${ap} ${u.active ? "active" : "inactive"}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      // Role filter
      if (filterRole !== "__all__" && u.role_id !== filterRole) return false;
      // Active/Inactive filter
      if (filterActive === "active" && !u.active) return false;
      if (filterActive === "inactive" && u.active) return false;
      // Project access filter
      if (filterProject !== "__all__") {
        const ap = Array.isArray(u.allowed_projects) ? u.allowed_projects : [];
        if (filterProject === "__none__") {
          // Users with full (no restriction) access
          if (ap.length !== 0) return false;
        } else {
          if (ap.length === 0) return false; // restricted to no projects? skip when filtering by specific
          if (!ap.includes(filterProject)) return false;
        }
      }
      return true;
    });
  }, [users, q, filterRole, filterActive, filterProject]);

  const {
    pageItems: pagedUsers,
    page: uPage, setPage: setUPage,
    pageSize: uPageSize, setPageSize: setUPageSize,
    totalPages: uTotalPages, totalItems: uTotalItems,
  } = usePagination(filteredUsers, "voltledger.users.pageSize");

  const activeFilterCount = (filterRole !== "__all__" ? 1 : 0) + (filterActive !== "__all__" ? 1 : 0) + (filterProject !== "__all__" ? 1 : 0);
  const clearAllFilters = () => {
    setQ("");
    setFilterRole("__all__");
    setFilterActive("__all__");
    setFilterProject("__all__");
  };

  const startCreate = () => {
    setForm({ ...EMPTY });
    setEditId(null);
    setOpen(true);
  };

  const startEdit = (u) => {
    setForm({
      email: u.email, name: u.name, password: "",
      role_id: u.role_id, active: u.active,
      allowed_projects: Array.isArray(u.allowed_projects) ? u.allowed_projects : [],
    });
    setEditId(u.id);
    setOpen(true);
  };

  const toggleProject = (name) => {
    setForm((f) => {
      const set = new Set(f.allowed_projects);
      if (set.has(name)) set.delete(name); else set.add(name);
      return { ...f, allowed_projects: Array.from(set) };
    });
  };

  const submit = async () => {
    try {
      if (editId) {
        await api.put(`/users/${editId}`, {
          name: form.name,
          role_id: form.role_id,
          active: form.active,
          allowed_projects: form.allowed_projects,
          ...(form.password ? { password: form.password } : {}),
        });
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1300px]">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-red-600/90">Administration</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Users</h1>
          <p className="text-sm text-slate-500 mt-2">
            Manage user accounts, role assignments and project-level access.
            {q && <span className="ml-2 text-indigo-600 font-semibold">· {filteredUsers.length} match{filteredUsers.length === 1 ? "" : "es"}</span>}
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="relative w-72">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input
              placeholder="Search name, email, role, project…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-none pl-9 pr-9 h-10 text-sm border-slate-300 focus-visible:ring-[#002FA7] focus-visible:border-[#002FA7]"
              data-testid="users-search"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-3 top-3 text-slate-400 hover:text-slate-700 text-xs font-semibold"
                data-testid="users-search-clear"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={startCreate} data-testid="new-user-btn" className="bg-[#002FA7] hover:bg-[#00227A] rounded-none h-10">
              <Plus className="w-4 h-4 mr-1.5" /> New User
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">{editId ? "Edit User" : "Create User"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Email *</Label><Input value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="rounded-none" disabled={!!editId} data-testid="user-email" /></div>
              <div><Label>Name *</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="rounded-none" data-testid="user-name-input" /></div>
              <div><Label>{editId ? "Password (leave blank to keep)" : "Password *"}</Label><Input type="password" value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} className="rounded-none" data-testid="user-password" /></div>
              <div>
                <Label>Role *</Label>
                <Select value={form.role_id} onValueChange={(v)=>setForm({...form, role_id:v})}>
                  <SelectTrigger className="rounded-none" data-testid="user-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>{roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Project Access</Label>
                <div className="border border-slate-200 p-3 max-h-48 overflow-y-auto">
                  {projects.length === 0 ? (
                    <div className="text-xs text-slate-400">No projects defined. Add projects first.</div>
                  ) : (
                    projects.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1">
                        <Checkbox
                          checked={form.allowed_projects.includes(p.name)}
                          onCheckedChange={() => toggleProject(p.name)}
                          data-testid={`user-project-${p.name}`}
                        />
                        <span>{p.name}</span>
                        {!p.active && <span className="text-[10px] text-slate-400">(disabled)</span>}
                      </label>
                    ))
                  )}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">
                  Empty = full access to all projects. Tick to restrict this user to specific projects.
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <input type="checkbox" id="active" checked={form.active} onChange={(e)=>setForm({...form, active:e.target.checked})} data-testid="user-active" />
                <Label htmlFor="active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-none">Cancel</Button>
              <Button onClick={submit} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none" data-testid="submit-user">{editId ? "Update" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 px-4 py-3 mb-3 flex items-center flex-wrap gap-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Filters</div>
        <div className="flex items-center gap-2">
          <Label className="text-[11px] text-slate-600">Role</Label>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="rounded-none h-9 w-48" data-testid="users-filter-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All roles</SelectItem>
              {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[11px] text-slate-600">Status</Label>
          <Select value={filterActive} onValueChange={setFilterActive}>
            <SelectTrigger className="rounded-none h-9 w-40" data-testid="users-filter-active"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="inactive">Inactive only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[11px] text-slate-600">Project Access</Label>
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="rounded-none h-9 w-56" data-testid="users-filter-project"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any project access</SelectItem>
              <SelectItem value="__none__">Full access (no restriction)</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.name}>Restricted · {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-slate-500">
            Showing <strong className="text-slate-900">{filteredUsers.length}</strong> of {users.length}
          </span>
          {(activeFilterCount > 0 || q) && (
            <button
              onClick={clearAllFilters}
              className="text-[11px] font-semibold uppercase tracking-wider text-[#002FA7] hover:underline"
              data-testid="users-filter-clear"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      <div className="card-surface overflow-x-auto rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Name</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Email</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Role</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Project Access</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 && <tr><td colSpan={6} className="text-center text-sm text-slate-400 py-12">{(q || activeFilterCount > 0) ? "No users match the current filters." : "No users yet."}</td></tr>}
            {pagedUsers.map((u) => {
              const ap = Array.isArray(u.allowed_projects) ? u.allowed_projects : [];
              return (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50 align-top">
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 text-slate-700">{u.email}</td>
                  <td className="px-4 py-3"><span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{u.role_name}</span></td>
                  <td className="px-4 py-3">
                    {ap.length === 0 ? (
                      <span className="chip-soft info">All projects</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {ap.map((p) => (
                          <span key={p} className="px-2 py-0.5 text-[10px] bg-indigo-50 text-indigo-700 rounded">{p}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`status-pill ${u.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {u.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                    <button onClick={() => startEdit(u)} className="text-slate-500 hover:text-[#002FA7]" data-testid={`edit-user-${u.email}`}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => del(u.id)} className="text-red-500 hover:text-red-700" data-testid={`del-user-${u.email}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination
          page={uPage} setPage={setUPage}
          pageSize={uPageSize} setPageSize={setUPageSize}
          totalPages={uTotalPages} totalItems={uTotalItems}
          testId="users-pagination"
        />
      </div>
    </div>
  );
}
